import { ApiRoutes, AppRoutes } from 'src/lib/routes';
import { NextSeo } from 'next-seo';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import ImageWithFallback from 'src/components/ImageWithFallback';
import { CurveText } from 'src/components/CurveText';
import Curation1Gif from 'public/images/about/curation_1.gif';
import Curation2Gif from 'public/images/about/curation_2.gif';
import Curation3Gif from 'public/images/about/curation_3.gif';
import Curation4Gif from 'public/images/about/curation_4.gif';
import Curation5Gif from 'public/images/about/curation_5.gif';
import CurationThumbnail1Image from 'public/images/about/curation_thumbnail_1.png';
import CurationThumbnail2Image from 'public/images/about/curation_thumbnail_2.png';
import CurationThumbnail3Image from 'public/images/about/curation_thumbnail_3.png';
import CurationThumbnail4Image from 'public/images/about/curation_thumbnail_4.png';
import CurationThumbnail5Image from 'public/images/about/curation_thumbnail_5.png';
import OurCurationHeroImage from 'public/images/about/our_curation_hero.png';
import OurCurationHero1Image from 'public/images/about/our_curation_hero_1.png';
import styles from 'src/styles/modules/pages/brand.module.scss';
import { useEffect, useRef, useState } from 'react';
import { Tips } from 'src/components/Tips';
import { getEleOffset } from 'src/helpers';
import useQuery from 'src/hooks/useQuery';
import { NumericFormat } from 'react-number-format';
import { useTranslations } from 'next-intl';
import { prepareTranslations } from 'src/lib/prepare_translations';
import { GetStaticProps } from 'next';
import { Translation } from 'src/types';
import useAddLocaleToUrl from 'src/hooks/useAddLocaleToUrl';

const STATIC_REVIEWS = [
  { name: 'Stella', message: 'The warmth and love I experienced gave me faith in humanity.' },
  { name: 'Rogerio', message: 'One of the best tours of my life' },
  { name: 'Hosanna', message: 'Hosted by wonderful people with genuine hearts.' },
  { name: 'Ben', message: "If you're looking for something different to do away from the touristy spots, this is it!" },
  { name: 'Matthew', message: 'I refer to these kind of helpers that you come across on your trips as "Travel Angels." Seek Sophie is definitely one of these.' },
  { name: 'Maggy', message: 'OMG MY HEART, really no words.' },
  { name: 'Olivia', message: 'Booking with Seek Sophie is the best decision we made' },
  { name: 'Michelle', message: 'Seek Sophie showed me a different side of travel. It felt like exploring with heart, not just checking off a checklist.' },
  { name: 'Martin F', message: "Every trip has been a hit. Can't recommend it enough!" },
  { name: 'Danielle', message: 'Being able to trek off the beaten path made us feel like we could see the land for its beauty without being covered with tourists.' },
  { name: 'Kelly', message: 'Seek Sophie captures the soul of each destination' }
]

const tips = (translation: Translation) => ([
  {
    title: translation('howAreAllExerience'),
    tipCardColor: 'tw-bg-orange-light',
    content: (
      <>
        <div className="tw-mb-2">
          {translation('simpleWeDont')}
        </div>
        <div>
          {translation('andIfAnExperience')}
        </div>
      </>
    ),
  },
  {
    title: translation('whyShouldIChoose'),
    tipCardColor: 'tw-bg-brand-green',
    content: (
      <>
        <div className="tw-mb-2">
          {translation('weAreAllergic')}
        </div>
        <div>
          {translation('whileCheaperTours')}
        </div>
      </>
    ),
  },
  {
    title: translation('whyAreSomePopular'),
    tipCardColor: 'tw-bg-brand-yellow',
    content: (
      <>
        <div className="tw-mb-2">
          {translation('weHanpickEveryExperience')}
        </div>
        <div>
          {translation('chancesAreWeChecked')}
        </div>
      </>
    ),
  },
]);

export const getStaticProps: GetStaticProps = async (context) => {
  const translations = await prepareTranslations(context, ['pages/our-curation']);

  return { props: translations }
};

const Page = () => {
  const addLocaleToUrl = useAddLocaleToUrl()
  const translation = useTranslations('pages/our-curation')
  const divRef = useRef<HTMLDivElement | null>(null);
  const items = ['#gift-1', '#gift-2', '#gift-3', '#gift-4', '#gift-5'];
  const [activeGifId, setActiveGifId] = useState('');
  useEffect(() => {
    if (!divRef.current) return;
    const divRefEle = divRef.current;
    const findActiveItem = () => {
      const scrollPos = window.scrollY;
      items.map((target) => {
        const sectionEle: HTMLElement | null = document.querySelector(target);
        if (!sectionEle) return;
        var positionTop =
          getEleOffset(sectionEle).top - divRefEle.offsetHeight - sectionEle.clientHeight;

        if (positionTop <= scrollPos && positionTop + sectionEle.offsetHeight > scrollPos) {
          setActiveGifId(target);
        }
      });
    };

    document.addEventListener('scroll', findActiveItem);

    return () => {
      document.removeEventListener('scroll', findActiveItem);
    };
  }, []);

  const { data } = useQuery<{
    customersCount: Number;
  }>(ApiRoutes.apiTotalCustomersRoute({}, {}).toUrl(), {
    fetcherOpts: {
      corsEnable: true,
    },
  });

  const { customersCount } = data || { customersCount: 0 };

  return (
    <>
      <NextSeo
        title={translation('metaTitle')}
        description={translation('metaDescription')}
        canonical={addLocaleToUrl(AppRoutes.ourCurationRoute().toUrl())}
        openGraph={{
          url: addLocaleToUrl(AppRoutes.ourCurationRoute().toUrl()),
          title: translation('metaTitle'),
          description: translation('metaDescription'),
        }}
      />
      <WithDefaultJsonLd />

      <div className="sr-only">
        {STATIC_REVIEWS.map((review, index) => (
          <div key={index}>
            <div>Review Author: {review.name}</div>
            <div>Review Body: {review.message}</div>
          </div>
        ))}
      </div>

      <div className="tw-bg-brand-green tw-py-16 md:tw-py-24">
        <div ref={divRef}></div>
        <div className="tw-w-full tw-px-6 md:tw-px-10 tw-z-10">
          <h1 className="tw-font-styleSans tw-text-6xl md:tw-text-8xl tw-font-semibold tw-leading-none md:tw-leading-none tw-text-center md:tw-mb-8 tw-mb-0">
            {translation('onlyTheGoodStuff')}
          </h1>
          <div className="md:tw-hidden tw-block tw-py-8">
            <ImageWithFallback
              src={OurCurationHero1Image}
              alt=""
              className={`${styles.curationHeroImage} tw-block tw-static tw-object-contain`}
            />
          </div>
          <div className="tw-flex tw-justify-center tw-items-center">
            <div className="md:tw-max-w-lg tw-max-w-sm tw-text-center md:tw-text-lg">
              {translation('weHandpickEveryGuide')}
            </div>
          </div>
        </div>
        <div className="md:tw-block tw-hidden">
          <ImageWithFallback
            src={OurCurationHeroImage}
            alt=""
            className={`${styles.curationHeroImage} tw-block tw-static tw-object-contain`}
          />
        </div>
      </div>

      <div className="tw-bg-brand-yellow">
        <div className="container tw-text-center tw-max-w-4xl md:tw-text-lg tw-pt-16 md:tw-pt-24">
          <h2 className="md:tw-max-w-md tw-max-w-xs tw-mx-auto">
            <CurveText
              radius={0.7}
              id="how_it_works"
              key={translation('showItWorks')}
              text={translation('showItWorks')}
              className="md:tw-text-8xl tw-text-6xl tw-font-semibold tw-leading-none md:tw-leading-none tw-font-styleSans"
            />
          </h2>
        </div>

        <div className="container tw-max-w-4xl md:tw-text-lg tw-pt-16 md:tw-pt-24">
          <div className="row row-original tw-px-6 tw-text-center md:tw-mb-0 tw-mb-11">
            <div className="col-md-6 md:tw-order-1 tw-order-2 tw-flex tw-justify-center tw-items-center">
              <div>
                <div className="tw-text-sm tw-font-semibold tw-mb-6 md:tw-block tw-hidden">
                  {translation('step', {step: 1})}
                </div>
                <div className="tw-text-6xl tw-font-styleSans tw-font-semibold md:tw-block tw-hidden tw-mb-6">
                  {translation('workTheGround')}
                </div>
                <div className="md:tw-mb-6 tw-mb-4">
                  {translation('beforeWeAddAnyExperience')}
                </div>
              </div>
            </div>

            <div className="col-md-6 md:tw-order-2 tw-order-1 tw-text-center tw-mb-4 md:tw-mb-0 md:tw-px-0 tw-px-6">
              <div className="md:tw-hidden tw-block">
                <div className="tw-text-sm tw-font-semibold tw-mb-4">{translation('step', {step: 1})}</div>
                <div className="tw-text-5xl tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-mb-4">
                  {translation('workTheGround')}
                </div>
              </div>
              <ImageWithFallback
                src={Curation1Gif}
                alt={translation('altCurationGiftImage1')}
                className="tw-hidden"
                priority={true}
              />
              <ImageWithFallback
                id="gift-1"
                src={activeGifId === '#gift-1' ? Curation1Gif : CurationThumbnail1Image}
                alt={translation('altCurationGiftImage1')}
                className={`${styles.curationGiftImage1} tw-block tw-static tw-object-cover`}
              />
            </div>
          </div>

          <div className="row row-original tw-text-center tw-mb-11">
            <div className="col-md-6 tw-order-2 tw-flex tw-justify-center tw-items-center">
              <div>
                <div className="tw-text-sm tw-font-semibold tw-mb-6 md:tw-block tw-hidden">
                  {translation('step', {step: 2})}
                </div>
                <div className="tw-text-6xl tw-font-styleSans tw-font-semibold md:tw-block tw-hidden tw-mb-6">
                  {translation('meetTheHost')}
                </div>
                <div className="md:tw-mb-6 tw-mb-4">
                  {translation('weKnowTheSecret')}
                </div>
              </div>
            </div>

            <div className="col-md-6 md:tw-order-1 tw-order-1 tw-text-center tw-mb-4 md:tw-mb-0 md:tw-px-0 tw-px-20">
              <div className="md:tw-hidden tw-block">
                <div className="tw-text-sm tw-font-semibold tw-mb-4">{translation('step', {step: 2})}</div>
                <div className="tw-text-5xl tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none">
                  {translation('meetTheHost')}
                </div>
              </div>
              <ImageWithFallback
                src={Curation2Gif}
                alt={translation('altCurationGiftImage2')}
                className="tw-hidden"
                priority={true}
              />
              <ImageWithFallback
                src={activeGifId === '#gift-2' ? Curation2Gif : CurationThumbnail2Image}
                id="gift-2"
                alt={translation('altCurationGiftImage2')}
                className={`${styles.curationGiftImage2} tw-block tw-static tw-object-cover`}
              />
            </div>
          </div>

          <div className="row row-original tw-px-6 tw-text-center tw-mb-11">
            <div className="col-md-6 md:tw-order-1 tw-order-2 tw-flex tw-justify-center tw-items-center">
              <div>
                <div className="tw-text-sm tw-font-semibold tw-mb-6 md:tw-block tw-hidden">
                  {translation('step', {step: 3})}
                </div>
                <div className="tw-text-6xl tw-font-styleSans tw-font-semibold md:tw-block tw-hidden tw-mb-6">
                  {translation('testExperiences')}
                </div>
                <div className="md:tw-mb-6 tw-mb-4">
                  {translation('weDontJustReseach')}
                </div>
              </div>
            </div>

            <div className="col-md-6 md:tw-order-2 tw-order-1 tw-text-center tw-mb-4 md:tw-mb-0 md:tw-px-0 tw-px-9">
              <div className="md:tw-hidden tw-block">
                <div className="tw-text-sm tw-font-semibold tw-mb-4">{translation('step', {step: 3})}</div>
                <div className="tw-text-5xl tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-mb-4">
                  {translation('testExperiences')}
                </div>
              </div>
              <ImageWithFallback
                src={Curation3Gif}
                alt={translation('altCurationGiftImage3')}
                className="tw-hidden"
                priority={true}
              />
              <ImageWithFallback
                src={activeGifId === '#gift-3' ? Curation3Gif : CurationThumbnail3Image}
                id="gift-3"
                alt={translation('altCurationGiftImage3')}
                className={`${styles.curationGiftImage3} tw-block tw-static tw-object-cover`}
              />
            </div>
          </div>

          <div className="row row-original tw-text-center tw-mb-11">
            <div className="col-md-6 tw-order-2 tw-flex tw-justify-center tw-items-center">
              <div>
                <div className="tw-text-sm tw-font-semibold tw-mb-6 md:tw-block tw-hidden">
                  {translation('step', {step: 4})}
                </div>
                <div className="tw-text-6xl tw-font-styleSans tw-font-semibold md:tw-block tw-hidden tw-mb-6">
                  {translation('mysteryShopping')}
                </div>
                <div className="md:tw-mb-6 tw-mb-4">
                  {translation('weSecretlyShopOurOwn')}
                </div>
              </div>
            </div>

            <div className="col-md-6 md:tw-order-1 tw-order-1 tw-text-center tw-mb-4 md:tw-mb-0 md:tw-px-0 tw-px-6">
              <div className="md:tw-hidden tw-block">
                <div className="tw-text-sm tw-font-semibold tw-mb-4">{translation('step', {step: 4})}</div>
                <div className="tw-text-5xl tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none">
                  {translation('mysteryShopping')}
                </div>
              </div>
              <ImageWithFallback
                src={Curation4Gif}
                alt={translation('altCurationGiftImage4')}
                className="tw-hidden"
                priority={true}
              />
              <ImageWithFallback
                src={activeGifId === '#gift-4' ? Curation4Gif : CurationThumbnail4Image}
                id="gift-4"
                alt={translation('altCurationGiftImage4')}
                className={`${styles.curationGiftImage4} tw-block tw-static tw-object-cover`}
              />
            </div>
          </div>

          <div className="row row-original tw-px-6 tw-text-center md:tw-mb-0 tw-mb-11">
            <div className="col-md-6 md:tw-order-1 tw-order-2 tw-flex tw-justify-center tw-items-center">
              <div>
                <div className="tw-text-sm tw-font-semibold tw-mb-6 md:tw-block tw-hidden">
                  {translation('step', {step: 5})}
                </div>
                <div className="tw-text-6xl tw-font-styleSans tw-font-semibold md:tw-block tw-hidden tw-mb-6">
                  {translation('fineTuning')}
                </div>
                <div className="md:tw-mb-6 tw-mb-4">
                  {translation('weObsessOverReviews')}
                </div>
              </div>
            </div>

            <div className="col-md-6 md:tw-order-2 tw-order-1 tw-text-center tw-mb-4 md:tw-mb-0 md:tw-px-0 tw-px-6">
              <div className="md:tw-hidden tw-block">
                <div className="tw-text-sm tw-font-semibold tw-mb-4">{translation('step', {step: 5})}</div>
                <div className="tw-text-5xl tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-mb-4">
                  {translation('fineTuning')}
                </div>
              </div>
              <ImageWithFallback
                src={Curation5Gif}
                alt={translation('altCurationGiftImage5')}
                className="tw-hidden"
                priority={true}
              />
              <ImageWithFallback
                src={activeGifId === '#gift-5' ? Curation5Gif : CurationThumbnail5Image}
                id="gift-5"
                alt={translation('altCurationGiftImage5')}
                className={`${styles.curationGiftImage5} tw-block tw-static tw-object-cover`}
              />
            </div>
          </div>
        </div>

        <div className="tw-bg-brand-green md:tw-mt-24 tw-mt-16 tw-relative tw-h-full">
          <img
            className={`tw-relative tw-static ${styles.whoTrustSSImage} tw-object-cover md:tw-object-contain`}
          />
          <div className="tw-left-0 tw-right-0 tw-top-0 tw-absolute tw-z-10 tw-w-full tw-h-full tw-flex tw-justify-center md:tw-items-center tw-items-start md:tw-pt-0 tw-pt-16">
            <div className="tw-text-center">
              <h2 className="md:tw-text-8xl tw-font-styleSans md:tw-max-w-xl tw-text-5xl tw-max-w-xs tw-font-semibold md:tw-leading-none tw-leading-none">
                {translation('endResult')} <br />
                <NumericFormat
                  value={Number(customersCount)}
                  displayType="text"
                  thousandSeparator={','}
                />{' '}
                {translation('people')}
                <br /> {translation('whoTrustSS')}
              </h2>
            </div>
          </div>
        </div>

        <div className="tw-bg-brand-navy tw-py-24">
          <div className="container tw-px-0 md:tw-pl-0 tw-pl-6">
            <h2 className="tw-font-semibold tw-leading-none tw-text-5xl tw-mb-16 md:tw-text-6xl tw-font-styleSans tw-text-brand-green tw-text-center tw-max-w-xs md:tw-max-w-full tw-mx-auto">
              {translation('thingsYouAreDyingToKnow')}
              <br /> {translation('butMayNotAsk')}
            </h2>
            <Tips tips={tips(translation)} tipCardClass={`${styles.tip} tw-mr-7`} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Page;
